import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useCallback, useEffect, useRef, useState} from 'react'

import {getKeyrusAiLocaleKey} from 'helpers/keyrusAi'

import introVideo from './assets/keyrus-intro.mp4'
import introVideoEs from './assets/keyrus-intro-es.mp4'
import introVideoPt from './assets/keyrus-intro-pt.mp4'

const CLOSE_LABEL = 'Close video'
const EXPAND_LABEL = 'Expand video'
const MUTE_LABEL = 'Mute'

// Localized popup controls. Brazilian Portuguese (pt) is client-specified;
// es/fr are sensible defaults. en is the fallback.
const VIDEO_LABELS = {
  en: {skip: 'Skip video', skipShort: 'Skip', sound: 'Tap for sound'},
  fr: {skip: 'Passer la vidéo', skipShort: 'Passer', sound: 'Activer le son'},
  es: {
    skip: 'Saltar el video',
    skipShort: 'Saltar',
    sound: 'Toca para activar el sonido',
  },
  pt: {skip: 'Pular vídeo', skipShort: 'Pular', sound: 'Ative o som'},
}

// The LATAM/Spain cut carries burned-in Spanish subtitles; the pt-br cut
// carries burned-in Portuguese subtitles ("Legendado").
const VIDEO_BY_LOCALE = {es: introVideoEs, pt: introVideoPt}

const EDGE_GAP = 24
const PIP_WIDTH_DESKTOP = 340
const PIP_WIDTH_MOBILE = 200
const POPUP_MAX_WIDTH = 1180
const ASPECT = 9 / 16

const MORPH_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

// Persists across remounts within the same page session so a navigation-driven
// re-render/remount can't reopen the intro once the user has dismissed it
// (mirrors the gateway's `gatewayRuntime.dismissed` guard).
const introRuntime = {seen: false}

const computeGeometry = (mode, isMobile) => {
  if (typeof window === 'undefined') {
    return null
  }

  const vw = window.innerWidth
  const vh = window.innerHeight

  if (mode === 'pip') {
    const width = Math.min(
      isMobile ? PIP_WIDTH_MOBILE : PIP_WIDTH_DESKTOP,
      vw - EDGE_GAP * 2,
    )
    const height = width * ASPECT

    return {
      width,
      height,
      left: vw - width - EDGE_GAP,
      top: vh - height - EDGE_GAP,
      radius: isMobile ? 12 : 16,
    }
  }

  const widthByViewport = Math.min(
    POPUP_MAX_WIDTH,
    vw * (isMobile ? 0.92 : 0.86),
  )
  const heightCap = vh * 0.82
  const width = Math.min(widthByViewport, heightCap / ASPECT)
  const height = width * ASPECT

  return {
    width,
    height,
    left: (vw - width) / 2,
    top: (vh - height) / 2,
    radius: isMobile ? 16 : 24,
  }
}

const SkipIcon = ({size}) => (
  <svg
    aria-hidden="true"
    fill="currentColor"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 5l9 7-9 7V5zm12 0h2.4v14H17V5z" />
  </svg>
)

SkipIcon.propTypes = {size: PropTypes.number}
SkipIcon.defaultProps = {size: 18}

const ExpandIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="34"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2.2"
    viewBox="0 0 24 24"
    width="34"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M9 21H3v-6M3 21l7-7M15 3h6v6M21 3l-7 7" />
  </svg>
)

const CloseIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="13"
    stroke="currentColor"
    strokeLinecap="round"
    strokeWidth="1.8"
    viewBox="0 0 14 14"
    width="13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 1l12 12M13 1L1 13" />
  </svg>
)

const SpeakerOnIcon = ({size}) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
    <path
      d="M16.5 8.8a4.5 4.5 0 0 1 0 6.4M19 6.5a8 8 0 0 1 0 11"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.8"
    />
  </svg>
)

SpeakerOnIcon.propTypes = {size: PropTypes.number}
SpeakerOnIcon.defaultProps = {size: 20}

const SpeakerMutedIcon = ({size}) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
    <path
      d="M16.5 9.5l5 5M21.5 9.5l-5 5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.8"
    />
  </svg>
)

SpeakerMutedIcon.propTypes = {size: PropTypes.number}
SpeakerMutedIcon.defaultProps = {size: 20}

const KeyrusAiVideoIntro = ({isMobile, nodeLocale}) => {
  const theme = useTheme()
  const localeKey = getKeyrusAiLocaleKey(nodeLocale)
  const labels = VIDEO_LABELS[localeKey] || VIDEO_LABELS.en
  const videoSrc = VIDEO_BY_LOCALE[localeKey] || introVideo
  const videoRef = useRef(null)
  const [mode, setMode] = useState(() =>
    introRuntime.seen ? 'closed' : 'popup',
  )
  const [geometry, setGeometry] = useState(null)
  const [pipHover, setPipHover] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  const headerZIndex = theme.customZindex?.header || 1000000
  const baseZIndex = headerZIndex + 20
  // In PiP the video must sit BELOW the mobile nav overlay (header + 10) so an
  // open menu covers it — otherwise menu taps land on the PiP and re-open it.
  const pipZIndex = headerZIndex + 5
  const cyan = theme.palette?.primary?.main || '#28c9ff'

  useEffect(() => {
    if (mode === 'closed') {
      return undefined
    }

    const update = () => setGeometry(computeGeometry(mode, isMobile))

    update()
    window.addEventListener('resize', update)

    return () => window.removeEventListener('resize', update)
  }, [mode, isMobile])

  useEffect(() => {
    // Autoplay is only reliable while muted; the "Tap for sound" button
    // unmutes on the user's tap (browsers block audio before any gesture).
    const video = videoRef.current

    if (video) {
      video.muted = true
      const attempt = video.play()

      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {})
      }
    }
  }, [])

  const toggleSound = useCallback(() => {
    const video = videoRef.current

    if (!video) {
      return
    }

    const nextMuted = !video.muted

    video.muted = nextMuted

    if (!nextMuted) {
      const attempt = video.play()

      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {})
      }
    }

    setIsMuted(nextMuted)
  }, [])

  const skip = useCallback(() => {
    const video = videoRef.current

    if (video) {
      video.muted = true
    }

    introRuntime.seen = true
    setIsMuted(true)
    setPipHover(false)
    setMode('pip')
  }, [])

  const expand = useCallback(() => {
    const video = videoRef.current

    if (video) {
      video.muted = false
      const playback = video.play()

      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {})
      }
    }

    setIsMuted(false)
    setPipHover(false)
    setMode('popup')
  }, [])

  const close = useCallback(() => {
    const video = videoRef.current

    if (video) {
      video.pause()
    }

    introRuntime.seen = true
    setMode('closed')
  }, [])

  if (mode === 'closed') {
    return null
  }

  const isPopup = mode === 'popup'
  // On mobile the controls sit at the TOP and are kept compact so they never
  // overlap the video's burned-in subtitles (which live at the bottom).
  const ctrlHeight = isMobile ? 32 : 48
  const ctrlFontSize = isMobile ? 12 : 16
  const ctrlEdge = isMobile ? 10 : 24
  const soundToggleSize = isMobile ? 32 : 48
  const soundIconSize = isMobile ? 16 : 20
  const skipIconSize = isMobile ? 14 : 18
  const soundCtaPadding = isMobile ? '0 12px' : '0 20px'
  const skipPadding = isMobile ? '0 12px' : '0 22px'
  const soundCtaGap = isMobile ? 6 : 9

  // Tap-anywhere-to-unmute: clicking the popup video area unmutes when muted.
  // Extracted into named variables to avoid nested ternary lint errors.
  const popupOnClick = isMuted ? toggleSound : undefined
  const popupOnKeyDown = isMuted
    ? event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggleSound()
        }
      }
    : undefined
  const pipOnKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      expand()
    }
  }
  const wrapperOnClick = isPopup ? popupOnClick : expand
  const wrapperOnKeyDown = isPopup ? popupOnKeyDown : pipOnKeyDown
  const wrapperCursor = isPopup && !isMuted ? 'default' : 'pointer'
  const wrapperIsInteractive = !isPopup || isMuted
  const wrapperRole = wrapperIsInteractive ? 'button' : undefined
  const wrapperTabIndex = wrapperIsInteractive ? 0 : undefined
  const wrapperAriaLabel = isPopup ? labels.sound : EXPAND_LABEL

  const wrapperStyle = geometry
    ? {
        position: 'fixed',
        top: geometry.top,
        left: geometry.left,
        width: geometry.width,
        height: geometry.height,
        borderRadius: geometry.radius,
      }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: `min(86vw, ${POPUP_MAX_WIDTH}px)`,
        aspectRatio: '16 / 9',
        transform: 'translate(-50%, -50%)',
        borderRadius: 24,
      }

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(7, 8, 20, 0.66)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: baseZIndex - 1,
          opacity: isPopup ? 1 : 0,
          pointerEvents: isPopup ? 'auto' : 'none',
          transition: 'opacity 0.45s ease',
        }}
      />

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onClick={wrapperOnClick}
        onKeyDown={wrapperOnKeyDown}
        onMouseEnter={isPopup ? undefined : () => setPipHover(true)}
        onMouseLeave={isPopup ? undefined : () => setPipHover(false)}
        role={wrapperRole}
        style={{
          ...wrapperStyle,
          overflow: 'hidden',
          background: '#000000',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)',
          zIndex: isPopup ? baseZIndex : pipZIndex,
          cursor: wrapperCursor,
          transition: `top 0.55s ${MORPH_EASE}, left 0.55s ${MORPH_EASE}, width 0.55s ${MORPH_EASE}, height 0.55s ${MORPH_EASE}, border-radius 0.55s ${MORPH_EASE}`,
        }}
        tabIndex={wrapperTabIndex}
        aria-label={wrapperAriaLabel}
      >
        <video
          ref={videoRef}
          onEnded={close}
          playsInline
          preload="auto"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>

        {isPopup && (
          <button
            aria-label={isMuted ? labels.sound : MUTE_LABEL}
            onClick={event => {
              event.stopPropagation()
              toggleSound()
            }}
            style={{
              position: 'absolute',
              left: ctrlEdge,
              top: isMobile ? ctrlEdge : undefined,
              bottom: isMobile ? undefined : 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMuted ? soundCtaGap : 0,
              height: ctrlHeight,
              width: isMuted ? 'auto' : soundToggleSize,
              padding: isMuted ? soundCtaPadding : 0,
              borderRadius: 999,
              border: 'none',
              background: isMuted ? cyan : 'rgba(0, 0, 0, 0.82)',
              color: isMuted ? '#07101f' : '#ffffff',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: ctrlFontSize,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: isMuted
                ? '0 8px 26px rgba(40, 201, 255, 0.55)'
                : 'none',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
            type="button"
          >
            {isMuted ? (
              <SpeakerMutedIcon size={soundIconSize} />
            ) : (
              <SpeakerOnIcon size={soundIconSize} />
            )}
            {isMuted && labels.sound}
          </button>
        )}

        {isPopup && (
          <button
            onClick={event => {
              event.stopPropagation()
              skip()
            }}
            style={{
              position: 'absolute',
              right: ctrlEdge,
              top: isMobile ? ctrlEdge : undefined,
              bottom: isMobile ? undefined : 24,
              display: 'inline-flex',
              alignItems: 'center',
              gap: isMobile ? 6 : 10,
              height: ctrlHeight,
              padding: skipPadding,
              borderRadius: 999,
              border: 'none',
              background: 'rgba(0, 0, 0, 0.82)',
              color: '#ffffff',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: ctrlFontSize,
              fontWeight: 600,
              cursor: 'pointer',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
            type="button"
          >
            {isMobile ? labels.skipShort : labels.skip}
            <SkipIcon size={skipIconSize} />
          </button>
        )}

        {!isPopup && (
          <>
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.4)',
                color: '#ffffff',
                opacity: pipHover ? 1 : 0,
                transition: 'opacity 0.25s ease',
                pointerEvents: 'none',
              }}
            >
              <ExpandIcon />
            </div>

            <button
              aria-label={CLOSE_LABEL}
              onClick={event => {
                event.stopPropagation()
                close()
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 999,
                border: 'none',
                background: 'rgba(0, 0, 0, 0.55)',
                color: '#ffffff',
                cursor: 'pointer',
                padding: 0,
              }}
              type="button"
            >
              <CloseIcon />
            </button>
          </>
        )}
      </div>
    </>
  )
}

KeyrusAiVideoIntro.propTypes = {
  isMobile: PropTypes.bool,
  nodeLocale: PropTypes.string,
}

KeyrusAiVideoIntro.defaultProps = {
  isMobile: false,
  nodeLocale: 'en-US',
}

export default KeyrusAiVideoIntro
